import axios from "axios";
import { PUBLIC_PATHS } from "../constants/publicPaths";

// Desktop app always uses production API
const API_BASE_URL = "/api";

// Helper to get just the base URL (without /api)
export const getApiBaseUrl = () => {
  return API_BASE_URL.replace('/api', '');
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Track if we're currently refreshing to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Graceful session expiry handler
const handleSessionExpired = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");

  // Dispatch custom event for session expiry (AuthContext listens for this)
  window.dispatchEvent(new CustomEvent('session-expired', {
    detail: { message: 'Your session has expired. Please log in again.' }
  }));

  // Don't redirect on public routes (signing, portal login, intake, etc.)
  const publicPaths = ['/login', ...PUBLIC_PATHS];
  const isPublicRoute = publicPaths.some(p => window.location.pathname.includes(p));
  if (!isPublicRoute) {
    window.location.href = '/login?expired=true';
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors with token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || "";
    const isAuthUrl = url.includes("/auth/login") || url.includes("/auth/refresh");

    if (error.response?.status === 401 && !isAuthUrl && !originalRequest._retry) {
      // Try to refresh the token first
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const newToken = response.data.access_token;
        localStorage.setItem("token", newToken);

        // Update the authorization header
        api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        processQueue(null, newToken);

        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - session is truly expired
        processQueue(refreshError, null);
        handleSessionExpired();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
